import { ProductEntry, SubscriptionInfoContainer } from '../../../store/api';
import { ExtendedManageSubscriptionOverview } from '../table/extended/ExtendedManageSubscriptionOverview';
import { formatUsageLimit } from '../../../shared/utils/formatUsageLimit';
import { ExtendedManageSubscriptionTableOverviewCell } from '../utils/subscriptionTableTileTypes';
import { mapIssueUsageOverviewAndShared } from '../../../shared/subscription-management/mappings/mapIssueUsage';
import { mapProductTitleWarningSymbol } from '../../../shared/subscription-management/mappings/mapProductTitleWarningSymbol';
import { getPatronSubscriptionsLabel } from '../../../shared/subscription-management/utils/getPatronSubscriptionsLabel';
import { issueNotDeliveredYet } from '../../../shared/subscription-management/types/IssueUsage';
import i18n from '../../../../i18n';

const determineUsageLimitOverview = (individualUsageLimit?: number | null, sharedUsageLimit?: number | null) => {
    if (individualUsageLimit === 0 || sharedUsageLimit === 0) {
        // early return when one subscription is unlimited
        return 0;
    }

    return (individualUsageLimit ?? 0) + (sharedUsageLimit ?? 0);
};

export const mapExtendedOverview = (item: ProductEntry): ExtendedManageSubscriptionOverview => {
    const {
        product,
        currentSubscriptions,
        nextSubscriptions,
        previousIssueStatistics,
        currentIssueStatistics,
        patronSubscriptions,
        expired,
        ceased,
        expirationDate
    } = item;

    return {
        product,
        userSubscribedLabel: getPatronSubscriptionsLabel(patronSubscriptions),
        showWarning: mapProductTitleWarningSymbol({
            previousLoanStatistics: previousIssueStatistics?.overview,
            currentLoanStatistics: currentIssueStatistics?.overview
        }),

        currentYearSubscription: expired
            ? {
                  type: ExtendedManageSubscriptionTableOverviewCell.Expired,
                  expirationDate
              }
            : ceased
            ? {
                  type: ExtendedManageSubscriptionTableOverviewCell.Ceased,
                  expirationDate
              }
            : currentSubscriptions?.individualUsageLimit == null && currentSubscriptions?.sharedUsageLimit == null
            ? {
                  type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitNone,
                  label: i18n.t('shop.subscriptions.noCurrentYearSubscription')
              }
            : {
                  type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitValue,
                  overviewLabel: formatUsageLimit(
                      determineUsageLimitOverview(
                          currentSubscriptions.individualUsageLimit,
                          currentSubscriptions.sharedUsageLimit
                      )
                  ),
                  individuellLabel: formatUsageLimit(currentSubscriptions.individualUsageLimit),
                  sharedLabel: formatUsageLimit(currentSubscriptions.sharedUsageLimit),
                  stockDetails: currentSubscriptions.stockDetails.overview
              },

        previousIssueUsage: hasSubscription(currentSubscriptions)
            ? previousIssueStatistics.overview
                ? mapIssueUsageOverviewAndShared(
                      previousIssueStatistics.overview,
                      currentSubscriptions?.sharedUsageLimit != null
                  )
                : issueNotDeliveredYet
            : undefined,

        currentIssueUsage: hasSubscription(currentSubscriptions)
            ? currentIssueStatistics.overview
                ? mapIssueUsageOverviewAndShared(
                      currentIssueStatistics.overview,
                      currentSubscriptions?.sharedUsageLimit != null
                  )
                : issueNotDeliveredYet
            : undefined,

        nextYearSubscription:
            nextSubscriptions?.individualUsageLimit == null && nextSubscriptions?.sharedUsageLimit == null
                ? {
                      type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitNone,
                      label: i18n.t('shop.subscriptions.notRenewedYet')
                  }
                : {
                      type: ExtendedManageSubscriptionTableOverviewCell.UsageLimitValue,
                      overviewLabel: formatUsageLimit(
                          determineUsageLimitOverview(
                              nextSubscriptions.individualUsageLimit,
                              nextSubscriptions.sharedUsageLimit
                          )
                      ),
                      individuellLabel: formatUsageLimit(nextSubscriptions.individualUsageLimit),
                      sharedLabel: formatUsageLimit(nextSubscriptions.sharedUsageLimit),
                      stockDetails: nextSubscriptions.stockDetails.overview
                  },

        expired,
        ceased
    };
};

const hasSubscription = (subscription?: SubscriptionInfoContainer) =>
    subscription?.individualUsageLimit != null || subscription?.sharedUsageLimit != null;
